import { ArrowDown, ArrowUp } from "@phosphor-icons/react"

import { formatCurrency, formatNumber } from "~/utils/formatting"
import { cn } from "~/utils/helpers"

// Shared formatting + delta-coloring helpers for ChatMetricBlock (the unified
// number / KPI renderer) and any other consumer that needs the same number/delta
// styling. Kept separate so the DeltaPill component and tone helpers can be reused
// without dragging in the full block renderer.

export type Format = "integer" | "decimal" | "currency" | "percent"
export type GoodWhen = "increasing" | "decreasing" | "neutral"

export function formatValue(value: number, format: Format, currency: string): string {
  switch (format) {
    case "currency":
      return formatCurrency(value, currency)
    case "percent":
      return `${formatNumber(value, 1)}%`
    case "decimal":
      return formatNumber(value, 2)
    case "integer":
    default:
      return formatNumber(Math.round(value))
  }
}

// Resolves the colour class for a delta given which direction is "good" for this metric.
// Undefined goodWhen → muted: a missing direction means we can't safely tell whether the
// change is positive or negative for the business, so don't guess (an LLM omission would
// previously have rendered as "increasing", which is wrong for inverted metrics like
// return rate).
export function deltaToneClass(value: number, goodWhen?: GoodWhen): string {
  if (!goodWhen || goodWhen === "neutral") return "text-fg-subtle"
  const positive = value >= 0
  const isGood = goodWhen === "decreasing" ? !positive : positive
  return isGood ? "text-green-600" : "text-red-600"
}

export function DeltaPill({ percent, goodWhen }: { percent: number; goodWhen?: GoodWhen }) {
  const positive = percent >= 0
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 paragraph-sm",
        deltaToneClass(percent, goodWhen),
      )}
    >
      {positive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
      <span>{formatNumber(Math.abs(percent), 1)}%</span>
    </span>
  )
}
